import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog";
import {
    X,
    Plus,
    User,
    Clock,
    Check,
    CheckCircle,
    XCircle,
    ListTodo,
    Pencil,
    CalendarIcon,
} from "lucide-react";
import { Button } from "../../../Components/UI/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../Components/UI/tooltip";
import { Badge } from "../../../Components/UI/badge";
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import { getTaskHistoryAPI } from '../../Tasks/TasksAPI';
import { toast } from "sonner";
import { getDateWithoutTime } from '../../../Utils/Utilities';
import { getPriorityDetails } from '../../../Utils/Constants';

const TaskHistoryDialog = ({
    isOpen,
    onClose,
    enrichedConnection,
    threadData = [],
    threadTypes = [],
    getExecutiveName,
    getRelativeDateText,
    onTaskEntryOpen,
    getPriorityInfo,
    getColorClass,
    getTaskColor
}) => {
    const [taskHistoryData, setTaskHistoryData] = useState([]);
    const [loadingTaskHistory, setLoadingTaskHistory] = useState(false);

    // Fetch completed tasks when dialog opens
    useEffect(() => {
        if (isOpen && enrichedConnection?.ConnID && taskHistoryData.length === 0) {
            fetchTaskHistory();
        }
    }, [isOpen, enrichedConnection?.ConnID]);

    const fetchTaskHistory = async () => {
        setLoadingTaskHistory(true);
        try {
            const now = new Date();
            const historyApiResponse = await getTaskHistoryAPI({ year: now.getFullYear(), month: now.getMonth() + 1 });

            let tasksData = [];
            if (historyApiResponse?.Table1 && Array.isArray(historyApiResponse.Table1)) {
                tasksData = historyApiResponse.Table1;
            }

            // Keep only tasks belonging to this connection, and only completed (2) or cancelled (0) ones
            const connID = enrichedConnection?.ConnID;
            const completedTasks = tasksData.filter(task =>
                Number(task.ConnID) == Number(connID) && (task.Status == 2 || task.Status == 0)
            );

            setTaskHistoryData(completedTasks);
        } catch (error) {
            console.error('Error fetching task history:', error);
            toast.error('Failed to fetch task history');
            setTaskHistoryData([]);
        } finally {
            setLoadingTaskHistory(false);
        }
    };

    // Reset data when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setTaskHistoryData([]);
        }
    }, [isOpen]);
    const getTaskStatusInfo = (status) => {
        const statusMap = {
            2: {
                name: "Completed",
                icon: CheckCircle, 
                className: "text-sahi-green bg-sahi-green-light border-sahi-green" 
            },
            0: { 
                name: "Cancelled", 
                icon: XCircle, 
                className: "text-sahi-red bg-sahi-red-light border-sahi-red" 
            }
        };
        
        return statusMap[status] || { 
            name: "Unknown", 
            icon: Clock, 
            className: "text-sub-text-color bg-sahi-disable border-sahi-border-color" 
        };
    };

    // Helper function to format thread display
    const formatThreadDisplay = (threadIDs) => {
        if (!threadIDs || !threadIDs.trim()) return null;

        const threadIdArray = threadIDs.split(',').map(id => id.trim());

        return threadIdArray.map((threadId, index) => {
            const thread = threadData.find(t => t.ThreadID?.toString() === threadId);
            if (!thread) return null;

            const threadType = threadTypes.find(type => type.TID === thread.TypeID);
            const typeName = threadType?.Typ || 'Thread';
            const sinceDT = thread.SinceDT ? getRelativeDateText(thread.SinceDT) : '';
            const productName = thread.MainProduct || 'No product specified';

            return (
                <TooltipProvider key={threadId}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="text-foreground font-semibold text-xs mr-1 mb-1">
                                {typeName} - {sinceDT}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{productName}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            );
        }).filter(Boolean);
    };

    // Helper function to get priority display
    const getPriorityDisplay = (priority) => {
        return getPriorityDetails(priority);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="!max-w-[600px]">
                <DialogHeader className="border-b border-sahi-border-color pb-4 flex-shrink-0">
                    <div className="flex items-center justify-between w-full">
                        <DialogTitle className="text-xl text-color">
                            Task History{taskHistoryData.length > 0 ? ` (${taskHistoryData.length})` : ''}
                        </DialogTitle>
                        <div className="flex items-center gap-2">
                            <DialogClose
                                onClick={onClose}
                                className="h-9 w-9 p-0 rounded-md hover:bg-bg-grey flex items-center justify-center"
                            >
                                <X className="h-4 w-4 text-color" />
                            </DialogClose>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-4 pb-4 scrollbar-no-arrows">
                    {loadingTaskHistory ? (
                        <div className="text-center py-12 text-sub-text-color">
                            <div className="h-8 w-8 animate-spin rounded-full border-2 border-sahi-blue border-t-transparent mx-auto mb-4"></div>
                            <p className="text-sm">Loading task history...</p>
                        </div>
                    ) : taskHistoryData.length > 0 ? (
                        <div className="space-y-3">
                            {taskHistoryData
                                .sort((a, b) => new Date(b.StatusDT || b.UpdatedDT) - new Date(a.StatusDT || a.UpdatedDT))
                                .map((task, index) => {
                                    const statusInfo = getTaskStatusInfo(task.Status);
                                    const priorityInfo = getPriorityDisplay(task.Priority);
                                    const StatusIcon = statusInfo.icon;

                                    return (
                                        <div key={task.TaskID || index} className="bg-white sahi-radius p-3 border border-sahi-border-color relative">
                                            {/* Header row with task title and status */}
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex-1">
                                                    <h4 className="text-sm font-semibold text-color mb-1">
                                                        {task.Task || 'Untitled Task'}
                                                    </h4>
                                                    <div className="flex items-center gap-2 text-xs text-sub-text-color">
                                                        <span>
                                                            {task.TaskType || 'General'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {priorityInfo.showBadge && (() => {
                                                        const PriorityIcon = priorityInfo.icon;
                                                        return (
                                                            <span
                                                                className="inline-flex items-center rounded px-2.5 py-0.5 text-xs font-semibold border"
                                                                style={{
                                                                    color: priorityInfo.color,
                                                                    backgroundColor: priorityInfo.bgColor,
                                                                    borderColor: priorityInfo.color
                                                                }}
                                                            >
                                                                <PriorityIcon className="h-3 w-3 mr-1" style={{ color: priorityInfo.color }} />
                                                                {priorityInfo.label}
                                                            </span>
                                                        );
                                                    })()}
                                                    <Badge
                                                        variant="secondary"
                                                        className={`${statusInfo.className} text-xs`}
                                                    >
                                                        <StatusIcon className="h-3 w-3 mr-1" />
                                                        {statusInfo.name}
                                                    </Badge>
                                                </div>
                                            </div>

                                            {/* Task description */}
                                            {task.Description && task.Description.trim() && (
                                                <div className="mb-3">
                                                    <p className="text-sm text-color">
                                                        {task.Description}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Due date and completion info */}
                                            <div className="flex items-center justify-between">
                                                <div className="text-xs text-sub-text-color">
                                                    <span className="text-muted-foreground">Due: </span>
                                                    <span className={getColorClass(getTaskColor(task.DueDT))}>
                                                        {getDateWithoutTime(task.DueDT)}
                                                    </span>
                                                </div>
                                                <Badge
                                                    variant="secondary"
                                                    className="bg-sahi-disable text-sub-text-color border-sahi-border-color hover:bg-sahi-disable text-xs"
                                                >
                                                    <User className="h-3 w-3 mr-1" />
                                                    {getExecutiveName(task.POCUserID)}
                                                </Badge>
                                            </div>

                                            {/* Thread Display */}
                                            {task.ThreadIDs && task.ThreadIDs.trim() && formatThreadDisplay(task.ThreadIDs) && formatThreadDisplay(task.ThreadIDs).length > 0 && (
                                                <div className="text-xs text-sub-text-color mt-2">
                                                    <span className="text-muted-foreground">Thread: </span>
                                                    {formatThreadDisplay(task.ThreadIDs)}
                                                </div>
                                            )}

                                            {/* Completion details - StatusDT if set, otherwise fall back to UpdatedDT */}
                                            <div className="mt-2 pt-2 border-t border-sahi-border-color">
                                                <div className="text-xs text-sub-text-color">
                                                    <span className="text-muted-foreground">
                                                        {task.Status == 2 ? 'Completed' : 'Cancelled'} on:
                                                    </span>
                                                    <span className="ml-1">
                                                        {(task.StatusDT || task.UpdatedDT) ? format(new Date(task.StatusDT || task.UpdatedDT), "MMM dd, yyyy 'at' hh:mm a") : '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-sub-text-color">
                            <ListTodo className="h-16 w-16 mb-4 text-sahi-grey" />
                            <p className="text-lg font-medium mb-2">No completed tasks found</p>
                            <p className="text-sm text-sahi-grey mb-4">Complete your first task to see it here</p>
                            <Button
                                onClick={onTaskEntryOpen}
                                className="bg-sahi-blue hover:bg-sahi-blue/90"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Create first task
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TaskHistoryDialog;